import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { 
  Mountain, Search, MapPin, Users, Star, Filter, Home, Compass, 
  Calendar, ChevronRight, Heart, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StaysListing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    location: searchParams.get('location') || '',
    guests: searchParams.get('guests') || '',
    minPrice: '',
    maxPrice: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    // Fetch with URL params on initial load
    fetchListings({
      location: searchParams.get('location') || '',
      guests: searchParams.get('guests') || ''
    });
  }, [searchParams]);

  const fetchListings = async (searchFilters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchFilters.location) params.append('location', searchFilters.location);
      if (searchFilters.guests) params.append('guests', searchFilters.guests);
      if (searchFilters.minPrice) params.append('min_price', searchFilters.minPrice);
      if (searchFilters.maxPrice) params.append('max_price', searchFilters.maxPrice);

      const url = `${API_URL}/api/stays${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        setListings(data.listings || []);
      }
    } catch (error) {
      console.error('Error fetching listings:', error);
    }
    setLoading(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchListings(filters);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Mountain className="h-8 w-8 text-[#2C5F8D]" />
              <span className="text-xl font-bold text-[#2C5F8D]" style={{fontFamily: 'Playfair Display, serif'}}>
                GoHimalayan
              </span>
            </Link>
            
            {/* Navigation Tabs */}
            <div className="hidden md:flex items-center gap-1 bg-gray-100 p-1 rounded-full">
              <Link to="/stays" className="flex items-center gap-2 px-4 py-2 bg-white rounded-full text-sm font-medium shadow-sm">
                <Home className="w-4 h-4" /> Stays
              </Link>
              <Link to="/treks" className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 rounded-full text-sm">
                <Compass className="w-4 h-4" /> Treks
              </Link>
              <Link to="/events" className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 rounded-full text-sm">
                <Calendar className="w-4 h-4" /> Events
              </Link>
            </div>

            <div className="flex items-center gap-3">
              <Link to="/partner" className="text-sm text-gray-600 hover:text-gray-900">
                Become a Host
              </Link>
              <Link to="/traveler/login">
                <Button variant="outline" size="sm">Sign In</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Search Bar */}
      <div className="bg-white border-b py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Where to? (Manali, Shimla, Leh...)"
                  value={filters.location}
                  onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-32">
              <div className="relative">
                <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="number"
                  placeholder="Guests"
                  value={filters.guests}
                  onChange={(e) => setFilters(prev => ({ ...prev, guests: e.target.value }))}
                  className="pl-10"
                  min="1"
                />
              </div>
            </div>
            <Button type="submit" className="bg-[#2C5F8D] hover:bg-[#1e4570]">
              <Search className="w-4 h-4 mr-2" /> Search
            </Button>
            <Button 
              type="button" 
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4 mr-2" /> Filters
            </Button>
          </form>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-xl flex flex-wrap gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Min Price (₹)</label>
                <Input
                  type="number"
                  value={filters.minPrice}
                  onChange={(e) => setFilters(prev => ({ ...prev, minPrice: e.target.value }))}
                  placeholder="Min"
                  className="w-32"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Max Price (₹)</label>
                <Input
                  type="number"
                  value={filters.maxPrice}
                  onChange={(e) => setFilters(prev => ({ ...prev, maxPrice: e.target.value }))}
                  placeholder="Max"
                  className="w-32"
                />
              </div>
              <div className="flex items-end">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setFilters({ location: '', guests: '', minPrice: '', maxPrice: '' });
                    fetchListings({});
                  }}
                >
                  Clear All
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Listings */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          {filters.location ? `Stays in ${filters.location}` : 'Explore Unique Stays'}
        </h1>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#2C5F8D]" />
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-20">
            <Home className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No stays found</h2>
            <p className="text-gray-500">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {listings.map(listing => (
              <Link 
                key={listing.listing_id} 
                to={`/stays/${listing.listing_id}`}
                className="group"
              >
                <div className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
                  {/* Image */}
                  <div className="relative aspect-[4/3] overflow-hidden">
                    {listing.images?.[0] ? (
                      <img 
                        src={listing.images[0]} 
                        alt={listing.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <Home className="w-12 h-12 text-gray-300" />
                      </div>
                    )}
                    <button className="absolute top-3 right-3 p-2 bg-white/80 rounded-full hover:bg-white">
                      <Heart className="w-5 h-5 text-gray-600" />
                    </button>
                    {listing.featured && (
                      <span className="absolute top-3 left-3 px-2 py-1 bg-[#2C5F8D] text-white text-xs rounded-full">
                        Discovered
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900 line-clamp-1">
                        {listing.title}
                      </h3>
                      {listing.rating > 0 && (
                        <div className="flex items-center gap-1 text-sm">
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          <span>{listing.rating.toFixed(1)}</span>
                        </div>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-500 mb-2">
                      {listing.location?.city}, {listing.location?.state}
                    </p>
                    
                    <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                      <span>{listing.max_guests} guests</span>
                      <span>•</span>
                      <span>{listing.bedrooms} bed</span>
                      <span>•</span>
                      <span>{listing.bathrooms} bath</span>
                    </div>

                    <div className="flex items-baseline gap-1">
                      <span className="text-lg font-bold text-gray-900">
                        ₹{listing.price_per_night?.toLocaleString()}
                      </span>
                      <span className="text-sm text-gray-500">/ night</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Mobile Navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t py-2 px-4">
        <div className="flex justify-around">
          <Link to="/stays" className="flex flex-col items-center text-[#2C5F8D]">
            <Home className="w-6 h-6" />
            <span className="text-xs mt-1">Stays</span>
          </Link>
          <Link to="/treks" className="flex flex-col items-center text-gray-400">
            <Compass className="w-6 h-6" />
            <span className="text-xs mt-1">Treks</span>
          </Link>
          <Link to="/events" className="flex flex-col items-center text-gray-400">
            <Calendar className="w-6 h-6" />
            <span className="text-xs mt-1">Events</span>
          </Link>
          <Link to="/traveler/login" className="flex flex-col items-center text-gray-400">
            <Users className="w-6 h-6" />
            <span className="text-xs mt-1">Profile</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default StaysListing;
